import {
    WidgetDocumentation,
    WidgetEvent,
    WidgetManifestEntry,
    WidgetParameterType,
    WidgetRenderMode,
} from '@/models/widget';
import {
    AddedWidget,
    SharedDesign,
    WidgetConfig,
} from '@/pages/widgetEditor/types';

/**
 * HTML attribute the editor auto-fills from the page URL rather than from the
 * manifest default. The product-filter widget hard-requires a lift operator
 * (`lift-operator-id`) and it is not adjustable, so we seed it from the
 * `liftOperatorId` query parameter — that's what makes the live preview render.
 */
export const LIFT_OPERATOR_ATTRIBUTE = 'lift-operator-id';

/** Query parameter carrying the lift operator this editor is scoped to. */
export const LIFT_OPERATOR_PARAM = 'liftOperatorId';

/**
 * Puts a previewed widget into sample mode: it authenticates as a seeded,
 * read-only sample account so auth-gated widgets (myTickets, myOrders,
 * myVouchers, myKeycards, myData) render their real component instead of a
 * login block. Set by `WidgetPreviewCard` on every previewed element.
 *
 * Preview only — it must never reach a customer's embed snippet. It is not a
 * manifest parameter, which is what keeps `buildSnippetAttributes` from ever
 * emitting it; `buildElementMarkup` below also strips it defensively, so the
 * guarantee holds even for a caller that hand-assembles attributes and calls
 * `buildCombinedSnippet` directly.
 *
 * The portal side reads this attribute name as a bare string with no shared
 * type or runtime handshake between the two repos — see
 * `apps/portal/src/widgets/shared/createWidgetElement.tsx` in
 * starjack-portal. If it renames the attribute there, both suites here stay
 * green and the preview silently reverts to a login block.
 */
export const SAMPLE_DATA_ATTRIBUTE = 'sample-data';

/**
 * Suggested fonts for `font` parameters. The manifest does not enumerate the
 * allowed fonts, so the control is `freeSolo` — these are convenient defaults
 * (Rethink Sans is the widgets' own default), but any family name is accepted.
 */
export const FONT_OPTIONS = [
    'Rethink Sans',
    'Inter',
    'Plus Jakarta Sans',
    'Roboto',
    'Open Sans',
    'Lato',
    'Montserrat',
    'Poppins',
    'Nunito',
    'Work Sans',
    'Source Sans 3',
    'Raleway',
    'system-ui',
    'Arial',
    'Helvetica',
    'Georgia',
    'Times New Roman',
];

/**
 * Google Fonts CSS2 stylesheet URL for a family name. This is the ONLY place
 * that builds this URL — the editor is the single source of truth for how a
 * font actually gets loaded. `buildAttributes`/`buildSnippetAttributes` below
 * attach it as the `font-url` attribute; starjack-portal's
 * `EmbedThemeProvider` (apps/portal/src/pages/embed/EmbedThemeProvider.tsx)
 * consumes that value verbatim to load fonts inside the auth iframe, which
 * cannot inherit a `<link>` from the host page's `<head>` the way the
 * shadow-DOM widgets do. Do not duplicate this formula on the portal side.
 */
export const buildGoogleFontHref = (family: string): string =>
    `https://fonts.googleapis.com/css2?family=${encodeURIComponent(
        family
    ).replace(/%20/g, '+')}:wght@400;500;600;700&display=swap`;

/**
 * Adds a derived `font-url` attribute next to `font-family`, if present. Kept
 * as a post-processing step rather than a manifest-declared parameter: the
 * operator never sets a font URL directly, it is always derived from whatever
 * font family they picked.
 */
const withFontUrl = (
    attributes: Record<string, string>
): Record<string, string> => {
    const fontFamily = attributes['font-family'];
    if (!fontFamily) return attributes;
    return { ...attributes, 'font-url': buildGoogleFontHref(fontFamily) };
};

/** `languageButton` / `fontFamily` -> `Language Button` / `Font Family`. */
export const humanizeLabel = (id: string): string =>
    id
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/^./, (char) => char.toUpperCase());

/** Look up a manifest entry by its `widget` id. */
export const findWidget = (
    widgets: WidgetManifestEntry[],
    widgetId: string
): WidgetManifestEntry | undefined =>
    widgets.find((widget) => widget.widget === widgetId);

/** Seed the config with every parameter's default value (empty if it has none). */
const buildDefaults = (widget: WidgetManifestEntry): WidgetConfig =>
    Object.fromEntries(
        Object.entries(widget.parameters).map(([name, param]) => [
            name,
            param.default ?? '',
        ])
    );

/**
 * Seed a fresh instance: manifest defaults, with context-provided parameters
 * (currently the lift-operator id) filled from the URL.
 */
export const buildInitialConfig = (
    widget: WidgetManifestEntry,
    liftOperatorId?: string
): WidgetConfig => {
    const config = buildDefaults(widget);
    if (liftOperatorId) {
        for (const [name, param] of Object.entries(widget.parameters)) {
            if (param.attribute === LIFT_OPERATOR_ATTRIBUTE) {
                config[name] = liftOperatorId;
            }
        }
    }
    return config;
};

/** Parameter names a widget exposes as design knobs (i.e. its adjustable ones). */
export const designParamNames = (widget: WidgetManifestEntry): string[] =>
    Object.entries(widget.parameters)
        .filter(([, param]) => param.adjustable)
        .map(([name]) => name);

/** A titled group of design fields, laid out in rows of at most two. */
export interface DesignSection {
    title: string;
    rows: string[][];
}

/**
 * Where each design knob sits in the editor. Layout is the editor's concern,
 * so it lives here rather than as a `group` field in the manifest.
 */
const DESIGN_SECTIONS: DesignSection[] = [
    { title: 'Schrift', rows: [['fontFamily', 'fontColor']] },
    { title: 'Farben', rows: [['primaryColor', 'secondaryColor']] },
    { title: 'Form', rows: [['radius', 'shadow']] },
];

const OTHER_SECTION_TITLE = 'Weitere';

/**
 * The given design knobs arranged into sections and rows. Knobs a widget does
 * not publish are dropped, along with any row or section left empty; knobs the
 * layout does not know yet land in a trailing section, two per row, so a new
 * knob in the manifest still shows up.
 */
export const groupDesignParams = (names: string[]): DesignSection[] => {
    const present = new Set(names);
    const placed = new Set<string>();

    const sections = DESIGN_SECTIONS.map(({ title, rows }) => ({
        title,
        rows: rows
            .map((row) => row.filter((name) => present.has(name)))
            .filter((row) => row.length > 0),
    })).filter(({ rows }) => rows.length > 0);

    sections.forEach(({ rows }) =>
        rows.flat().forEach((name) => placed.add(name))
    );

    const rest = names.filter((name) => !placed.has(name));
    if (rest.length > 0) {
        const rows: string[][] = [];
        for (let i = 0; i < rest.length; i += 2)
            rows.push(rest.slice(i, i + 2));
        sections.push({ title: OTHER_SECTION_TITLE, rows });
    }

    return sections;
};

/**
 * The design block shown once for the whole embed: the union of every widget's
 * adjustable parameters, seeded from their manifest defaults.
 *
 * A union rather than one widget's set, so the block does not depend on which
 * widget happened to be added first. The knobs are declared centrally in the
 * portal (`widgets/shared/design.ts`) and are therefore identical across
 * widgets today; first-wins on a conflicting default is a deliberate,
 * good-enough tiebreak, not an invariant to rely on.
 */
export const buildSharedDesign = (
    widgets: WidgetManifestEntry[]
): SharedDesign => {
    const design: SharedDesign = {};
    for (const widget of widgets) {
        for (const [name, param] of Object.entries(widget.parameters)) {
            if (param.adjustable && !(name in design)) {
                design[name] = param.default ?? '';
            }
        }
    }
    return design;
};

/**
 * The values an instance actually renders with: its own widget-specific config,
 * the shared design underneath it, and its overrides on top.
 *
 * Only keys the widget actually declares are taken from the shared design — a
 * widget must never receive an attribute it does not advertise.
 */
export const resolveConfig = (
    widget: WidgetManifestEntry,
    shared: SharedDesign,
    instance: AddedWidget
): WidgetConfig => {
    const config = { ...instance.config };
    for (const name of designParamNames(widget)) {
        const override = instance.hasDesignOverride
            ? instance.designOverrides[name]
            : undefined;
        const value = override ?? shared[name];
        if (value !== undefined) config[name] = value;
    }
    return config;
};

/** Resolve the current value of every parameter, keyed by its HTML attribute. */
export const buildAttributes = (
    widget: WidgetManifestEntry,
    config: WidgetConfig
): Record<string, string> =>
    withFontUrl(
        Object.fromEntries(
            Object.entries(widget.parameters).map(([name, param]) => [
                param.attribute,
                config[name] ?? param.default ?? '',
            ])
        )
    );

/**
 * The attributes worth writing into the embed snippet: everything the operator
 * actually chose, and nothing they left alone.
 *
 * Emitting unchanged defaults would pin them onto the customer's page — a
 * snippet pasted today would keep starjack's current palette forever, even for
 * an operator who never opened the design section. Omitting them lets those
 * pages track our defaults as they change.
 *
 * The preview keeps using `buildAttributes`: it needs a concrete value for
 * every parameter to render the element faithfully. Only the snippet omits.
 */
export const buildSnippetAttributes = (
    widget: WidgetManifestEntry,
    config: WidgetConfig
): Record<string, string> =>
    withFontUrl(
        Object.fromEntries(
            Object.entries(widget.parameters).flatMap(([name, param]) => {
                const value = config[name] ?? param.default ?? '';
                const fallback = param.default ?? '';
                return value === fallback ? [] : [[param.attribute, value]];
            })
        )
    );

/** The `<element>` markup for a single configured widget instance. */
const buildElementMarkup = (
    widget: WidgetManifestEntry,
    attributes: Record<string, string>
): string => {
    const attrLines = Object.entries(attributes)
        // Preview-only; see SAMPLE_DATA_ATTRIBUTE — it must never reach a
        // customer's page. HTML attribute names are case-insensitive, and
        // this exists precisely for a hand-assembling caller, so compare
        // case-insensitively rather than trusting an exact match.
        .filter(
            ([attribute]) => attribute.toLowerCase() !== SAMPLE_DATA_ATTRIBUTE
        )
        .map(([attribute, value]) => `    ${attribute}="${value}"`)
        .join('\n');

    return attrLines
        ? `<${widget.tag}\n${attrLines}\n></${widget.tag}>`
        : `<${widget.tag}></${widget.tag}>`;
};

/**
 * One deduped `<link>` per distinct `font-url` attribute across all instances.
 * This is what makes the shadow-DOM widgets' fonts load at all now that they
 * no longer self-fetch: a `<link>` in the host document is inherited by every
 * shadow root on the page. The auth iframe cannot see this link (a separate
 * document), which is why it also gets `font-url` forwarded as an attribute —
 * see `createIframeWidgetElement.tsx` in starjack-portal.
 */
const fontLinkLines = (
    instances: Array<{ attributes: Record<string, string> }>
): string[] => {
    const hrefs = Array.from(
        new Set(
            instances
                .map(({ attributes }) => attributes['font-url'])
                .filter((href): href is string => Boolean(href))
        )
    );

    return hrefs.length
        ? [
              '<!-- Schriftarten: vor dem Widget-Skript laden -->',
              ...hrefs.map((href) => `<link rel="stylesheet" href="${href}">`),
              '',
          ]
        : [];
};

/** The one tag that loads every widget of a version onto a page. */
export const buildEntryScriptTag = (entryUrl: string): string =>
    `<script type="module" src="${entryUrl}"></script>`;

/**
 * The full embed snippet for every added widget: font stylesheet links first,
 * then the version's module entry, then one custom element per instance.
 *
 * The entry is the only script, whatever the widgets: it finds the widget tags
 * on the page and fetches their chunks itself, so a per-widget script would
 * load a second copy of the code.
 */
export const buildCombinedSnippet = (
    instances: Array<{
        widget: WidgetManifestEntry;
        attributes: Record<string, string>;
    }>,
    entryUrl: string
): string => {
    if (instances.length === 0) return '';

    const fontLines = fontLinkLines(instances);

    const scriptLines = [
        '<!-- Starjack-Widgets: einmal pro Seite einbinden. Lädt nur den Code der Widgets, die auf dieser Seite stehen. -->',
        buildEntryScriptTag(entryUrl),
    ];

    const elementBlocks = [
        '<!-- Widgets: hier platzieren, wo sie erscheinen sollen -->',
        ...instances.map(({ widget, attributes }) =>
            buildElementMarkup(widget, attributes)
        ),
    ];

    return [...fontLines, ...scriptLines, '', ...elementBlocks].join('\n');
};

/** One row of the attribute table the docs dialog derives from the manifest. */
export interface DerivedAttribute {
    /** camelCase manifest key. */
    name: string;
    /** HTML attribute to set on the element. */
    attribute: string;
    type: WidgetParameterType;
    default?: string;
    adjustable: boolean;
    options?: string[];
    description?: string;
}

/** One event row, kept as the manifest publishes it. */
export interface DerivedEvent {
    name: string;
    event: WidgetEvent;
}

/**
 * Everything the documentation dialog needs about one widget: what the portal
 * authored, plus what can be derived from the manifest regardless.
 */
export interface ResolvedWidgetDoc {
    widget: WidgetManifestEntry;
    /** Human title, e.g. `My Tickets`. */
    label: string;
    /**
     * How the widget renders, as the manifest publishes it. Undefined means
     * the manifest does not publish a value for this widget yet — not that
     * it renders any particular way. The deployed manifest currently omits
     * this field for every widget, including the iframe one, so guessing a
     * default here would misclassify it.
     */
    renderMode: WidgetRenderMode | undefined;
    /**
     * Absolute URL of the version's module entry, which loads the element.
     * Absent until the manifest of a version has loaded.
     */
    scriptUrl?: string;
    /** Authored documentation, absent until the portal publishes it. */
    documentation?: WidgetDocumentation;
    attributes: DerivedAttribute[];
    /**
     * The same rows as `attributes`, keyed by the camelCase manifest name a
     * `WidgetDocStep.parameters` entry refers to. This is the one place that
     * maps a step's parameter name to its attribute/type/default — components
     * look the name up here instead of re-deriving the mapping themselves.
     */
    attributesByName: Record<string, DerivedAttribute>;
    events: DerivedEvent[];
}

/**
 * The one place that knows the documentation fallback rules.
 *
 * A widget without authored `documentation` is still fully described by its
 * parameters and events, so the dialog is useful before the portal ships a
 * single string — and every consumer reads the same merged shape either way.
 */
export const buildWidgetDoc = (
    widget: WidgetManifestEntry,
    entryUrl?: string
): ResolvedWidgetDoc => {
    const attributes: DerivedAttribute[] = Object.entries(
        widget.parameters
    ).map(([name, param]) => ({
        name,
        attribute: param.attribute,
        type: param.type,
        default: param.default,
        adjustable: param.adjustable,
        options: param.options,
        description: param.description,
    }));

    return {
        widget,
        label: humanizeLabel(widget.widget),
        // Passed through as-is: the deployed manifest predates this field
        // and publishes it for no widget yet, so an absent value must stay
        // absent rather than being defaulted to a guess.
        renderMode: widget.renderMode,
        scriptUrl: entryUrl,
        documentation: widget.documentation,
        attributes,
        attributesByName: Object.fromEntries(
            attributes.map((attribute) => [attribute.name, attribute])
        ),
        events: Object.entries(widget.events ?? {}).map(([name, event]) => ({
            name,
            event,
        })),
    };
};
